/**
 * Lightweight instrumentation for the file-processing pipeline.
 *
 * measure(name, fn) wraps a pipeline stage in a performance.measure so stage
 * timings show up in DevTools Performance recordings and can be read back via
 * performance.getEntriesByType('measure'). Handles sync and async fns; the
 * fn's result is returned unchanged.
 *
 * startLongTaskLogger() (dev only) logs every main-thread task over 50ms —
 * the budget browsers count against responsiveness before showing the
 * "page is slowing down" banner — so regressions are visible in the console
 * without a Performance recording.
 */

const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;

export function measure(name, fn) {
  if (typeof performance === 'undefined') return fn();

  const label = `rdrive:${name}`;
  const start = performance.now();
  const finish = () => {
    const duration = performance.now() - start;
    try {
      performance.measure(label, { start, duration });
    } catch {
      // Older performance.measure signatures (or jsdom) — timing loss only.
    }
    if (isDev) console.debug(`[perf] ${name}: ${duration.toFixed(1)}ms`);
  };

  let result;
  try {
    result = fn();
  } catch (error) {
    finish();
    throw error;
  }
  if (result && typeof result.then === 'function') {
    return result.finally(finish);
  }
  finish();
  return result;
}

let longTaskObserver = null;

export function startLongTaskLogger() {
  if (longTaskObserver || typeof PerformanceObserver === 'undefined') return;
  try {
    longTaskObserver = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        const source = entry.attribution?.[0]?.name;
        console.warn(
          `[perf] long task: ${Math.round(entry.duration)}ms${source && source !== 'unknown' ? ` (${source})` : ''}`
        );
      });
    });
    longTaskObserver.observe({ entryTypes: ['longtask'] });
  } catch {
    // 'longtask' unsupported (Firefox/Safari) — logger is Chrome-only.
    longTaskObserver = null;
  }
}
